<template>
  <v-card outlined rounded="xl">
    <v-form
      v-model="valid"
      ref="form"
      lazy-validation
      :readonly="!editMode"
      :disabled="!editMode"
      :class="{ 'form--is-disabled': !editMode }"
    >
      <!-- Impresa / Referente -->
      <v-card-text>
        <v-row>
          <v-col md="3">
            <div class="text-subtitle-1 font-weight-medium mb-6">Automezzo</div>

            <v-text-field
              v-model="form.vehicle_targa"
              label="Targa*"
              :rules="[(v) => !!v || 'Campo obbligatorio']"
              required
              maxlength="7"
              outlined
            />
            <v-select
              v-model="form.vehicle_province"
              label="Prov. immatricolazione*"
              :items="provinces"
              :rules="[(v) => !!v || 'Campo obbligatorio']"
              required
              outlined
              dense
            ></v-select>

            <v-text-field
              v-model.number="form.vehicle_load_capacity"
              label="Portata utile*"
              type="number"
              :rules="[(v) => !!v || 'Campo obbligatorio']"
              min="0"
              required
              suffix="q"
              outlined
              dense
            />
          </v-col>

          <v-divider vertical class="ml-4 mr-4"></v-divider>

          <v-col>
            <p class="text-subtitle-1 font-weight-medium mb-6">Rilevazione</p>

            <v-row no-gutters>
              <v-col sm="2">
                <v-select
                  v-model.number="form.year"
                  label="Anno*"
                  :items="[2020, 2021, 2022, 2023]"
                  :rules="[(v) => !!v || 'Campo obbligatorio']"
                  required
                  outlined
                ></v-select>
              </v-col>
              <v-col cols="10" class="pl-4">
                <v-select
                  v-model.number="form.week"
                  label="Settimana*"
                  :items="weekItems"
                  :disabled="!form.year"
                  :rules="[(v) => !!v || 'Campo obbligatorio']"
                  required
                  outlined
                ></v-select>
              </v-col>
              <v-col md="6" class="pr-2">
                <v-menu
                  v-model="menu1"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="form.valid_until"
                      label="Data scadenza compilazione*"
                      :rules="[(v) => !!v || 'Campo obbligatorio']"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      outlined
                      clearable
                      dense
                    ></v-text-field>
                  </template>
                  <v-date-picker v-model="form.valid_until" @input="menu1 = false"></v-date-picker>
                </v-menu>
              </v-col>
              <v-col md="6" class="pl-2">
                <v-menu
                  v-model="menu2"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="form.valid_from"
                      label="Inizio periodo compilazione (opzionale)"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      outlined
                      clearable
                      dense
                    ></v-text-field>
                  </template>
                  <v-date-picker v-model="form.valid_from" @input="menu2 = false"></v-date-picker>
                </v-menu>
              </v-col>
              <v-col cols="12">
                <v-text-field v-model="form.sample" label="Campione" outlined hide-details="auto" dense />
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions>
        <v-spacer></v-spacer>
        <template v-if="editMode">
          <v-btn text color="primary" @click="editMode = !editMode">Annulla</v-btn>
          <v-btn text color="primary" @click="onSubmit">Salva</v-btn>
        </template>
        <template v-else>
          <v-btn text color="primary" @click="editMode = !editMode">Modifica</v-btn>
        </template>
      </v-card-actions>
    </v-form>
  </v-card>
</template>

<script>
// Third-party libs
import { format, getQuarter } from 'date-fns'

// Data
import italyProvinces from '@/data/provinces.json'

// Helpers
import { getWeekDates } from '@/helpers'

// Validations
import { required } from '@/validations'

// Component
export default {
  name: 'EditSurveyInitialDataForm',
  props: {
    value: {
      type: Object,
    },
    readOnly: {
      type: Boolean,
      dafault: false,
    },
  },
  data() {
    return {
      dialog: false,
      menu1: false,
      menu2: false,
      valid: true,
      editMode: true,

      provinces: Object.keys(italyProvinces).sort(),

      form: {
        vehicle_targa: '',
        vehicle_province: '',
        vehicle_load_capacity: '',
        year: '',
        quarter: null,
        week: null,
        valid_from: null,
        valid_until: null,
        sample: '',
      },

      required,
    }
  },
  mounted() {
    this.editMode = !this.readOnly

    this.form.vehicle_targa = this.value?.vehicle_targa ?? ''
    this.form.vehicle_province = this.value?.vehicle_province ?? ''
    this.form.vehicle_load_capacity = this.value?.vehicle_load_capacity ?? null
    this.form.year = this.value?.year ?? null
    this.form.quarter = this.value?.quarter ?? null
    this.form.week = this.value?.week ?? null
    this.form.valid_until = this.value?.valid_until ?? null
    this.form.valid_from = this.value?.valid_from ?? null
    this.form.sample = this.value?.sample ?? ''
  },
  computed: {
    weekItems() {
      return Array.from({ length: 52 }, (value, index) => {
        if (!this.form.year) return []

        const weekNumber = index + 1
        const [startDate, endDate] = getWeekDates(this.form.year, weekNumber)

        const text = `${weekNumber} - dal ${format(startDate, 'dd/MM/yyyy')} al ${format(endDate, 'dd/MM/yyyy')}`

        return { value: weekNumber, text }
      })
    },
  },
  methods: {
    clean() {
      const [start, end] = getWeekDates(this.form.year, this.form.week)
      const quarter = getQuarter(start)

      return {
        ...this.form,
        quarter: quarter,
        start_date: format(start, 'yyyy-MM-dd'),
        end_date: format(end, 'yyyy-MM-dd'),
      }
    },
    async onSubmit() {
      await this.$refs.form.validate()

      if (this.valid) {
        const cleanedData = this.clean()

        this.$emit('submit', cleanedData)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
::v-deep {
  .form--is-disabled {
    .input-text-center input[type='number'] {
      text-align: center;
    }

    .theme--light.v-input--is-disabled input,
    .theme--light.v-select .v-select__selection--disabled {
      color: rgba(0, 0, 0, 0.85);
      -webkit-touch-callout: none; /* iOS Safari */
      -webkit-user-select: none; /* Safari */
      -khtml-user-select: none; /* Konqueror HTML */
      -moz-user-select: none; /* Old versions of Firefox */
      -ms-user-select: none; /* Internet Explorer/Edge */
      user-select: none;
      pointer-events: none;
      cursor: default;
    }

    .v-select > .v-input__control > .v-input__slot,
    .v-text-field > .v-input__control > .v-input__slot {
      cursor: default;
      color: rgba(0, 0, 0, 0.85);
      background-color: rgba(0, 0, 0, 0.03);
    }

    .v-select > .v-input__icon.v-input__icon--append {
      display: none;
    }

    .theme--light.v-label--is-disabled {
      color: rgba(0, 0, 0, 0.75);
    }
    .v-text-field--outlined fieldset {
      border: 1px solid rgba(0, 0, 0, 0.05);
      border: none;
    }

    .v-select__slot > .v-input__append-inner {
      display: none;
    }
  }
}
</style>
